import { Injectable } from "@angular/core";
import { ParamMap, Params } from "@angular/router";
import { ArrayParameter, ColorParameter, GameType, MagicAdvancedSearchParameters, RangeParameter } from "@apiserver-rest/ponder-data-client-base";
import { MagicAdvancedSearchFile } from "@apiserver-rest/ponder-tasks-client-base";

export enum AdvancedSearchParameter {
    Name = "Name",
    RulesText = "Rules Text",
    RulesTextRegex = "Rules Text (Regex)",
    FlavorText = "Flavor Text",
    ColorCost = "Color Cost",
    ColorProduction = "Color Production",
    ManaCost = "Mana Cost",
    CardSupertype = "Card Supertype",
    CardTypes = "Card Type",
    CardSubtypes = "Card Subtype",
    Rarities = "Rarity",
    Power = "Power",
    Toughness = "Toughness",
    ReleaseSets = "Release Set",
    ReleaseYear = "Release Year",
    Artists = "Artist",
    Treatments = "Treatment",
    Value = "Value",
    Legalities = "Legality"
};

@Injectable({ providedIn: "any" })
export class AdvancedSearchService {
    public parameters: MagicAdvancedSearchParameters = new MagicAdvancedSearchParameters();

    public hasAny(): boolean {
        return (this.parameters.name != undefined) || 
            (this.parameters.rulesText != undefined)|| 
            (this.parameters.rulesTextRegex != undefined)|| 
            (this.parameters.flavorText != undefined)|| 
            (this.parameters.colorCost != undefined)|| 
            (this.parameters.colorProduction != undefined)|| 
            (this.parameters.manaCost != undefined)|| 
            (this.parameters.cardSupertypes != undefined)|| 
            (this.parameters.cardTypes != undefined)|| 
            (this.parameters.cardSubtypes != undefined)|| 
            (this.parameters.rarities != undefined)|| 
            (this.parameters.power != undefined)|| 
            (this.parameters.toughness != undefined)|| 
            (this.parameters.releaseSets != undefined)|| 
            (this.parameters.releaseYear != undefined)|| 
            (this.parameters.artists != undefined)||
            (this.parameters.treatments != undefined)||
            (this.parameters.value != undefined)||
            (this.parameters.legalities!= undefined);
    }

    // -------------------------------------------------------------------------------------
    // To Query Params
    // -------------------------------------------------------------------------------------

    public toQueryParams(): Params {
        const supertypeCount = (this.parameters.cardSupertypes?.values?.length ?? 0);
        const typeCount = (this.parameters.cardTypes?.values?.length ?? 0);
        const subtypeCount = (this.parameters.cardSubtypes?.values?.length ?? 0);
        const rarityCount = (this.parameters.rarities?.values?.length ?? 0);
        const releaseSetCount = (this.parameters.releaseSets?.values?.length ?? 0);
        const artistCount = (this.parameters.artists?.values?.length ?? 0);
        const treatmentCount = (this.parameters.treatments?.values?.length ?? 0);
        const legalityCount = (this.parameters.legalities?.values?.length ?? 0);

        return {
            game: GameType.Magic,
            advanced: true,
            name: this.parameters.name,
            rules: this.parameters.rulesText,
            rulesRegex: this.parameters.rulesTextRegex,
            flavor: this.parameters.flavorText,
            mcw: this.parameters.colorCost?.white,
            mcu: this.parameters.colorCost?.blue,
            mcb: this.parameters.colorCost?.black,
            mcr: this.parameters.colorCost?.red,
            mcg: this.parameters.colorCost?.green,
            mcc: this.parameters.colorCost?.colorless,
            mcand: this.parameters.colorCost?.logicalAnd,
            mpw: this.parameters.colorProduction?.white,
            mpu: this.parameters.colorProduction?.blue,
            mpb: this.parameters.colorProduction?.black,
            mpr: this.parameters.colorProduction?.red,
            mpg: this.parameters.colorProduction?.green,
            mpc: this.parameters.colorProduction?.colorless,
            mpand: this.parameters.colorProduction?.logicalAnd,
            mcmin: this.parameters.manaCost?.min,
            mcmax: this.parameters.manaCost?.max,
            supertype0: (supertypeCount > 0 ? this.parameters.cardSupertypes!.values![0] : undefined),
            supertype1: (supertypeCount > 1 ? this.parameters.cardSupertypes!.values![1] : undefined),
            supertype2: (supertypeCount > 2 ? this.parameters.cardSupertypes!.values![2] : undefined),
            supertype3: (supertypeCount > 3 ? this.parameters.cardSupertypes!.values![3] : undefined),
            supertype4: (supertypeCount > 4 ? this.parameters.cardSupertypes!.values![4] : undefined),
            supertypeAnd: this.parameters.cardSupertypes?.logicalAnd,
            type0: (typeCount > 0 ? this.parameters.cardTypes!.values![0] : undefined),
            type1: (typeCount > 1 ? this.parameters.cardTypes!.values![1] : undefined),
            type2: (typeCount > 2 ? this.parameters.cardTypes!.values![2] : undefined),
            type3: (typeCount > 3 ? this.parameters.cardTypes!.values![3] : undefined),
            type4: (typeCount > 4 ? this.parameters.cardTypes!.values![4] : undefined),
            typeAnd: this.parameters.cardTypes?.logicalAnd,
            subtype0: (subtypeCount > 0 ? this.parameters.cardSubtypes!.values![0] : undefined),
            subtype1: (subtypeCount > 1 ? this.parameters.cardSubtypes!.values![1] : undefined),
            subtype2: (subtypeCount > 2 ? this.parameters.cardSubtypes!.values![2] : undefined),
            subtype3: (subtypeCount > 3 ? this.parameters.cardSubtypes!.values![3] : undefined),
            subtype4: (subtypeCount > 4 ? this.parameters.cardSubtypes!.values![4] : undefined),
            subtypeAnd: this.parameters.cardSubtypes?.logicalAnd,
            rarity0: (rarityCount > 0 ? this.parameters.rarities!.values![0] : undefined),
            rarity1: (rarityCount > 1 ? this.parameters.rarities!.values![1] : undefined),
            rarity2: (rarityCount > 2 ? this.parameters.rarities!.values![2] : undefined),
            rarity3: (rarityCount > 3 ? this.parameters.rarities!.values![3] : undefined),
            rarity4: (rarityCount > 4 ? this.parameters.rarities!.values![4] : undefined),
            rarityAnd: this.parameters.rarities?.logicalAnd,
            powerMin: this.parameters.power?.min,
            powerMax: this.parameters.power?.max,
            toughnessMin: this.parameters.toughness?.min,
            toughnessMax: this.parameters.toughness?.max,
            release0: (releaseSetCount > 0 ? this.parameters.releaseSets!.values![0] : undefined),
            release1: (releaseSetCount > 1 ? this.parameters.releaseSets!.values![1] : undefined),
            release2: (releaseSetCount > 2 ? this.parameters.releaseSets!.values![2] : undefined),
            release3: (releaseSetCount > 3 ? this.parameters.releaseSets!.values![3] : undefined),
            release4: (releaseSetCount > 4 ? this.parameters.releaseSets!.values![4] : undefined),
            releaseAnd: this.parameters.releaseSets?.logicalAnd,
            yearMin: this.parameters.releaseYear?.min,
            yearMax: this.parameters.releaseYear?.max,
            artist0: (artistCount > 0 ? this.parameters.artists!.values![0] : undefined),
            artist1: (artistCount > 1 ? this.parameters.artists!.values![1] : undefined),
            artist2: (artistCount > 2 ? this.parameters.artists!.values![2] : undefined),
            artist3: (artistCount > 3 ? this.parameters.artists!.values![3] : undefined),
            artist4: (artistCount > 4 ? this.parameters.artists!.values![4] : undefined),
            artistAnd: this.parameters.artists?.logicalAnd,
            treatment0: (treatmentCount > 0 ? this.parameters.treatments!.values![0] : undefined),
            treatment1: (treatmentCount > 1 ? this.parameters.treatments!.values![1] : undefined),
            treatment2: (treatmentCount > 2 ? this.parameters.treatments!.values![2] : undefined),
            treatment3: (treatmentCount > 3 ? this.parameters.treatments!.values![3] : undefined),
            treatment4: (treatmentCount > 4 ? this.parameters.treatments!.values![4] : undefined),
            treatmentAnd: this.parameters.treatments?.logicalAnd,
            valueMin: this.parameters.value?.min,
            valueMax: this.parameters.value?.max,
            legality0: (legalityCount > 0 ? this.parameters.legalities!.values![0] : undefined),
            legality1: (legalityCount > 1 ? this.parameters.legalities!.values![1] : undefined),
            legality2: (legalityCount > 2 ? this.parameters.legalities!.values![2] : undefined),
            legality3: (legalityCount > 3 ? this.parameters.legalities!.values![3] : undefined),
            legality4: (legalityCount > 4 ? this.parameters.legalities!.values![4] : undefined),
            legalityAnd: this.parameters.legalities?.logicalAnd,
        };
    }

    // -------------------------------------------------------------------------------------
    // From Query Params
    // -------------------------------------------------------------------------------------

    public fromQueryString(params: ParamMap): void {
        this.parameters.name = params.get("name") ?? undefined;
        this.parameters.rulesText = params.get("rules") ?? undefined;
        this.parameters.rulesTextRegex = params.get("rulesRegex") ?? undefined;
        this.parameters.flavorText = params.get("flavor") ?? undefined;
        this.parameters.colorCost = this.queryParamsToColor(params, "mcw", "mcu", "mcb", "mcr", "mcg", "mcc", "mcand");
        this.parameters.colorProduction = this.queryParamsToColor(params, "mpw", "mpu", "mpb", "mpr", "mpg", "mpc", "mpand");
        this.parameters.manaCost = this.queryParamsToRange(params, "mcmin", "mcmax");
        this.parameters.cardSupertypes = this.queryParamsToArray(params, "supertype");
        this.parameters.cardTypes = this.queryParamsToArray(params, "type");
        this.parameters.cardSubtypes = this.queryParamsToArray(params, "subtype");
        this.parameters.rarities = this.queryParamsToArray(params, "rarity");
        this.parameters.power = this.queryParamsToRange(params, "powerMin", "powerMax");
        this.parameters.toughness = this.queryParamsToRange(params, "toughnessMin", "toughnessMax");
        this.parameters.releaseSets = this.queryParamsToArray(params, "release");
        this.parameters.releaseYear = this.queryParamsToRange(params, "yearMin", "yearMax");
        this.parameters.artists = this.queryParamsToArray(params, "artist");
        this.parameters.treatments = this.queryParamsToArray(params, "treatment");
        this.parameters.value = this.queryParamsToRange(params, "valueMin", "valueMax");
        this.parameters.legalities = this.queryParamsToArray(params, "legality");
    }

    private queryParamsToColor(params: ParamMap, white: string, blue: string, black: string, red: string, green: string, colorless: string, logicalAnd: string): ColorParameter | undefined {
        var colorParam = new ColorParameter({
            white: (params.get(white) == "true"),
            blue: (params.get(blue) == "true"),
            black: (params.get(black) == "true"),
            red: (params.get(red) == "true"),
            green: (params.get(green) == "true"),
            colorless: (params.get(colorless) == "true"),
            logicalAnd: (params.get(logicalAnd) == "true")
        });

        return (
            colorParam.white ||
            colorParam.blue || 
            colorParam.black || 
            colorParam.red || 
            colorParam.green ||
            colorParam.colorless
        ) ? colorParam : undefined;
    }

    private queryParamsToRange(params: ParamMap, min: string, max: string): RangeParameter | undefined {
        return (params.has(min) && params.has(max)) ? new RangeParameter({ min: +params.get(min)!, max: +params.get(max)!}) : undefined;
    }

    private queryParamsToArray(params: ParamMap, prefix: string): ArrayParameter | undefined {
        const item0 = params.get(prefix + "0");
        const item1 = params.get(prefix + "1");
        const item2 = params.get(prefix + "2");
        const item3 = params.get(prefix + "3");
        const item4 = params.get(prefix + "4");
        const logicalAnd = params.get(prefix + "And") == "true";

        if (!item0 && !item1 && !item2 && !item3 && !item4) {
            return undefined;
        } else {
            var array = new ArrayParameter({ logicalAnd: logicalAnd, values: []});

            if (item0) { array.values!.push(item0); }
            if (item1) { array.values!.push(item1); }
            if (item2) { array.values!.push(item2); }
            if (item3) { array.values!.push(item3); }
            if (item4) { array.values!.push(item4); }

            return array;
        }
    }

    // -------------------------------------------------------------------------------------
    // Parameters Listing
    // -------------------------------------------------------------------------------------

    public compileParametersList(advancedSearchValues: MagicAdvancedSearchFile): string[] {
        var params: string[] = [];

        this.pushStringParameter(params, "name", this.parameters.name);
        this.pushStringParameter(params, "rules", this.parameters.rulesText);
        this.pushStringParameter(params, "rules regex", this.parameters.rulesTextRegex);
        this.pushStringParameter(params, "flavor", this.parameters.flavorText);
        this.pushColorParameter(params, "color cost", this.parameters.colorCost);
        this.pushColorParameter(params, "color production", this.parameters.colorProduction);
        this.pushRangeParameter(params, "mana cost", this.parameters.manaCost);
        this.pushArrayParameter(params, "supertype", this.parameters.cardSupertypes);
        this.pushArrayParameter(params, "type", this.parameters.cardTypes);
        this.pushArrayParameter(params, "subtype", this.parameters.cardSubtypes);
        this.pushArrayParameter(params, "rarity", this.parameters.rarities);
        this.pushRangeParameter(params, "power", this.parameters.power);
        this.pushRangeParameter(params, "toughness", this.parameters.toughness);
        this.pushArrayParameter(params, "set", this.parameters.releaseSets, advancedSearchValues.releaseSet);
        this.pushRangeParameter(params, "year", this.parameters.releaseYear);
        this.pushArrayParameter(params, "artist", this.parameters.artists, advancedSearchValues.artists);
        this.pushArrayParameter(params, "treatment", this.parameters.treatments, advancedSearchValues.treatments);
        this.pushRangeParameter(params, "value", this.parameters.value);
        this.pushArrayParameter(params, "legality", this.parameters.legalities, advancedSearchValues.legalities);

        return params;
    }

    private pushStringParameter(array: string[], name: string, value?: string): void {
        if (value) {
            array.push(`${name} LIKE '${value}'`);
        }
    }

    private pushColorParameter(array: string[], name: string, value?: ColorParameter): void {
        if (value) {
            var colors: string[] = [];

            if (value.white) { colors.push("white"); }
            if (value.blue) { colors.push("blue"); }
            if (value.black) { colors.push("black"); }
            if (value.red) { colors.push("red"); }
            if (value.green) { colors.push("green"); }
            if (value.colorless) { colors.push("colorless"); }

            if (colors.length) {
                array.push(`${name} INCLUDES (${colors.join((value.logicalAnd ? " AND " : " OR "))})`);
            }
        }
    }

    private pushRangeParameter(array: string[], name: string, value?: RangeParameter): void {
        if (value) {
            array.push(`${name} BETWEEN ${value.min} AND ${value.max}`);
        }
    }

    private pushArrayParameter(array: string[], name: string, value?: ArrayParameter, valueMap?: { [key: string]: string; }): void {
        if (value?.values) {
            var values = value.values;

            if (valueMap) {
                values = values.map(v => valueMap[v]);
            }

            array.push(`${name} INCLUDES (${values.join((value.logicalAnd ? " AND " : " OR "))})`);
        }
    }

    // -------------------------------------------------------------------------------------
    // Setters
    // -------------------------------------------------------------------------------------

    public setString(type: AdvancedSearchParameter, value?: string): void {
        switch (type) {
            case AdvancedSearchParameter.Name:
                this.parameters.name = value;
                break;

            case AdvancedSearchParameter.RulesText:
                this.parameters.rulesText = value;
                break;

            case AdvancedSearchParameter.RulesTextRegex:
                this.parameters.rulesTextRegex = value;
                break;

            case AdvancedSearchParameter.FlavorText:
                this.parameters.flavorText = value;
                break;

            default:
                break;
        }
    }
    
    public setRange(type: AdvancedSearchParameter, from?: number, to?: number): void {
        const value = ((from != undefined) && (to != undefined)) ? new RangeParameter({ min: from, max: to }) : undefined;

        switch (type) {
            case AdvancedSearchParameter.ManaCost:
                this.parameters.manaCost = value;
                break;

            case AdvancedSearchParameter.Power:
                this.parameters.power = value;
                break;

            case AdvancedSearchParameter.Toughness:
                this.parameters.toughness = value;
                break;

            case AdvancedSearchParameter.ReleaseYear:
                this.parameters.releaseYear = value;
                break;

            case AdvancedSearchParameter.Value:
                this.parameters.value = value;
                break;

            default:
                break;
        }
    }

    public setColor(type: AdvancedSearchParameter, logicalAnd: boolean, white: boolean, blue: boolean, black: boolean, red: boolean, green: boolean, colorless: boolean): void {
        const value = (white || blue || black || red || green || colorless) ? new ColorParameter({ white: white, blue: blue, black: black, red: red, green: green, colorless: colorless, logicalAnd: logicalAnd }) : undefined;

        switch (type) {
            case AdvancedSearchParameter.ColorCost:
                this.parameters.colorCost = value;
                break;

            case AdvancedSearchParameter.ColorProduction:
                this.parameters.colorProduction = value;
                break;

            default:
                break;
        }
    }

    public setArray(type: AdvancedSearchParameter, logicalAnd: boolean, values?: string[]): void
    {
        const value = (values != undefined) ? new ArrayParameter({ values: values, logicalAnd: logicalAnd }) : undefined;

        switch (type) {
            case AdvancedSearchParameter.CardSupertype:
                this.parameters.cardSupertypes = value;
                break;

            case AdvancedSearchParameter.CardTypes:
                this.parameters.cardTypes = value;
                break;

            case AdvancedSearchParameter.CardSubtypes:
                this.parameters.cardSubtypes = value;
                break;

            case AdvancedSearchParameter.Rarities:
                this.parameters.rarities = value;
                break;

            case AdvancedSearchParameter.ReleaseSets:
                this.parameters.releaseSets = value;
                break;

            case AdvancedSearchParameter.Artists:
                this.parameters.artists = value;
                break;

            case AdvancedSearchParameter.Treatments:
                this.parameters.treatments = value;
                break;

            case AdvancedSearchParameter.Legalities:
                this.parameters.legalities = value;
                break;

            default:
                break;
        }
    }
}